/**
 * Kaysou Assist — ligação do Hub da Mesa ao menu público.
 *
 * Isola o Hub do resto do menu: liga os hooks, decide quando abrir e
 * devolve o botão de reabertura quando o cliente está a ver o menu.
 * Não altera o fluxo de sessão, pedidos, checkout, KDS ou pagamento.
 */
import { useTableAssist } from "@/hooks/useTableAssist";
import { useTableHub } from "@/hooks/useTableHub";
import { assistLabel, assistTone } from "@/utils/tableAssistState";
import { TableHub } from "@/components/public/TableHub";
import { TableHubBill } from "@/components/public/TableHubBill";
import { useTablePayment } from "@/hooks/useTablePayment";

export function TableHubContainer({
  API, restaurant, restaurantId, isTableOrder, tableId, tableName, tableNumber,
  tableSession, sessionModalOpen = false,
  themeColor = "#f97316", fmt, source = "qr",
}) {
  const sessionId = tableSession?.session_id || null;
  const hub = useTableHub({
    API, restaurantId: restaurant?.id, tableId, sessionId, isTableOrder, sessionModalOpen,
  });
  const assist = useTableAssist({
    API, restaurantId: restaurant?.id, tableId, sessionId, source,
    enabled: hub.hubAvailable,
  });
  const tablePayment = useTablePayment({
    API, restaurantId: restaurant?.id, tableId, sessionId,
    requestId: assist.requests?.bill?.id,
    enabled: hub.showBill && !!assist.requests?.bill?.id,
  });

  if (!hub.hubAvailable) return null;

  const label = tableName || (tableNumber != null ? `MESA ${String(tableNumber).padStart(2, "0")}` : "MESA");

  const handleRequestBill = async () => {
    // As duas coisas ao mesmo tempo: abrir a conta (só leitura) e avisar o equipo.
    hub.setShowBill(true);
    hub.loadBill();
    await assist.callAssist("bill");
  };

  // Sem sessão ainda (ex.: cliente escolheu ver só o menu) a conta não existe;
  // o chamado continua a ser criado e a folha explica que não há consumo.
  const billEmptyNotice = sessionId ? "" : "Todavía no hay una cuenta abierta en esta mesa.";

  if (!hub.hubOpen) {
    // Durante o modal de sessão nada do Hub aparece — nem o atalho.
    if (sessionModalOpen) return null;
    return (
      <button
        type="button"
        onClick={hub.openHub}
        className="fixed bottom-24 right-3 z-[1000] px-4 h-11 rounded-full text-white text-sm font-bold shadow-lg transition active:scale-95"
        style={{ backgroundColor: themeColor, pointerEvents: "auto" }}
        data-testid="table-hub-reopen"
      >
        {label} · Asistencia
      </button>
    );
  }

  return (
    <>
      <TableHub
        restaurantName={restaurant?.name || ""}
        tableLabel={label}
        themeColor={themeColor}
        requests={assist.requests}
        sending={assist.sending}
        error={assist.error}
        onOrder={hub.closeHub}
        onCallWaiter={() => assist.callAssist("waiter")}
        onRequestBill={handleRequestBill}
        reviewUrl={restaurant?.google_review_url || ""}
        instagramUrl={restaurant?.instagram_url || ""}
        loyaltyUrl={`/portal/${restaurant?.slug || restaurantId}`}
      />
      {hub.showBill && (
        <TableHubBill
          bill={hub.bill}
          loading={hub.billLoading}
          error={hub.billError || billEmptyNotice}
          statusMessage={
            assistTone(assist.requests?.bill) === "idle" ? "" : assistLabel("bill", assist.requests?.bill)
          }
          payment={tablePayment.payment}
          paymentLoading={tablePayment.loading}
          paymentError={tablePayment.error}
          onPay={tablePayment.pay}
          onClose={() => hub.setShowBill(false)}
          fmt={fmt}
          themeColor={themeColor}
        />
      )}
    </>
  );
}
